import { queryOptions, useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { successStaleTime } from '@/shared/api/utils/query'
import { useCurrentTenant } from '@/shared/auth/store'
import { time } from '@/shared/datetime/utils/time'

import {
	fetchCaptureTask,
	fetchCollectorDetail,
	fetchCollectorSummaries,
	fetchCollectorVolume,
	fetchDiagnostic,
	fetchFleetVolume,
	fetchLogSourceOptions,
} from './api'
import { captureTaskPollInterval } from './capture-window'
import {
	detailSettlingInterval,
	hasCheckedIn,
	listSettlingInterval,
	SETTLING_POLL_INTERVAL,
} from './settling'
import { VOLUME_POLL_INTERVAL } from './volume'

import type { DiagnosticTask, LogSourceOnCollector } from './types'

/**
 * How often a waiting diagnostic is read again.
 *
 * The answer lands off a heartbeat, 60 seconds apart, so a poll much slower
 * than this could show a finished run most of a beat late; one much faster
 * only reads "not yet" more often.
 */
export const DIAGNOSTIC_POLL_INTERVAL = time(5, 's')

/**
 * `refetchInterval` for a diagnostic: poll only while it is waiting. A
 * fulfilled or expired task is final for this panel, and `null` (a task the
 * backend no longer has) has nothing to wait for.
 */
function diagnosticPollInterval(task: DiagnosticTask | null | undefined) {
	return task?.outcome === 'waiting' ? DIAGNOSTIC_POLL_INTERVAL : false
}

/**
 * Cache keys for the Network Collectors sub-feature.
 *
 * Its own root (`['collectors']`) rather than a branch of `integrationKeys`: a
 * Collector is not a Cloud integration, and hanging it off the integrations root
 * would make `invalidateIntegrations()` drop this cache for reasons that have
 * nothing to do with it.
 */
export const collectorKeys = {
	all: ['collectors'] as const,

	summaryLists: () => [...collectorKeys.all, 'summary-list'] as const,
	/**
	 * Keyed by tenant: `GET /agents` is tenant-scoped, and a key that omits the
	 * tenant serves one tenant's Collectors to the next after a tenant switch.
	 */
	summaryList: (tenant: string) =>
		[...collectorKeys.summaryLists(), tenant] as const,

	details: () => [...collectorKeys.all, 'detail'] as const,
	/** Tenant-scoped for the same reason the list is — see {@link collectorKeys.summaryList}. */
	detail: (tenant: string, collectorId: string) =>
		[...collectorKeys.details(), tenant, collectorId] as const,

	/** Tenant-scoped: the answer is the tenant's plan, not the user's. */
	logSourceOptions: (tenant: string) =>
		[...collectorKeys.all, 'log-source-options', tenant] as const,

	/** One diagnostic task. Keyed by task, not log source: there is no history route. */
	diagnostic: (tenant: string, taskId: string) =>
		[...collectorKeys.all, 'diagnostic', tenant, taskId] as const,

	volumes: () => [...collectorKeys.all, 'volume'] as const,
	/** The fleet figure and the per-collector one are separate calls, so separate keys. */
	fleetVolume: (tenant: string) =>
		[...collectorKeys.volumes(), 'fleet', tenant] as const,
	collectorVolume: (tenant: string, collectorId: string) =>
		[...collectorKeys.volumes(), 'collector', tenant, collectorId] as const,

	/** Tenant-scoped: a task id is only readable in the tenant that asked. */
	captureTask: (tenant: string, taskId: string) =>
		[...collectorKeys.all, 'capture-task', tenant, taskId] as const,
}

export const collectorQueries = {
	/**
	 * The Collectors **list page's** own read — `GET /agents`,
	 * mapped to `CollectorSummary`. Short `staleTime` because the whole point
	 * of the surface is liveness: a "last seen 3s ago" that is itself five
	 * minutes old is worse than no reading.
	 */
	summaryList: (tenant: string) =>
		queryOptions({
			queryKey: collectorKeys.summaryList(tenant),
			queryFn: ({ signal }) => fetchCollectorSummaries(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(5, 'm'),
			// Polls only while a row is settling, so a new collector's first beat
			// or a new listener's convergence shows without a reload.
			refetchInterval: ({ state }) => listSettlingInterval(state.data),
		}),

	/**
	 * One Collector and the Log Sources hosted on it — `GET /agents/{agent_id}`.
	 *
	 * Its own query rather than a `select` over the cached list: the detail page
	 * is reachable directly by URL, and this route carries fields the list row
	 * does not (`hostname`, each listener's `last_event_at`).
	 *
	 * Same short `staleTime` as the list — the page leads with liveness.
	 */
	detail: (tenant: string, collectorId: string) =>
		queryOptions({
			queryKey: collectorKeys.detail(tenant, collectorId),
			queryFn: ({ signal }) =>
				fetchCollectorDetail(tenant, collectorId, signal),
			staleTime: ({ state }) => successStaleTime(state, 30, 's'),
			gcTime: time(5, 'm'),
			refetchInterval: ({ state }) => detailSettlingInterval(state.data),
		}),

	/**
	 * The add-collector wizard's wait for a fresh collector's first check-in.
	 *
	 * The detail read under a different poll policy, on the detail's own key, so
	 * the page the wizard navigates to opens on a warm cache. Polls until the
	 * first heartbeat rather than until the collector settles — `enrolled` with
	 * a heartbeat settles but has not checked in. Polls in a background tab too:
	 * the engineer is in a terminal on another machine while this runs, and the
	 * caller bounds the wait, so it costs little.
	 */
	checkIn: (tenant: string, collectorId: string) =>
		queryOptions({
			...collectorQueries.detail(tenant, collectorId),
			refetchInterval: ({ state }) =>
				hasCheckedIn(state.data) ? false : SETTLING_POLL_INTERVAL,
			refetchIntervalInBackground: true,
		}),

	/**
	 * One Log Source — found by id inside its Collector's
	 * {@link collectorQueries.detail} answer, never fetched on its own.
	 *
	 * The collector's own key and query function, with a `select` on top: there
	 * is no single-listener read route, and sharing the entry keeps one cache
	 * entry per collector, so any log-source write invalidates it with the rest
	 * of the collectors prefix.
	 *
	 * `null` when the collector itself 404s; `source: null` when the collector
	 * exists but carries no listener with this id (see `LogSourceOnCollector`).
	 */
	source: (tenant: string, collectorId: string, sourceId: string) =>
		queryOptions({
			...collectorQueries.detail(tenant, collectorId),
			select: (collector): LogSourceOnCollector | null =>
				collector && {
					collector,
					source:
						collector.logSources.find(({ id }) => id === sourceId) ?? null,
				},
		}),

	/**
	 * The device families this tenant may add a Log Source for — and, when
	 * empty, the plan gate (`../plan-upsell.ts`). Long `staleTime`: a plan
	 * changes by contract, not while someone is on the page.
	 */
	logSourceOptions: (tenant: string) =>
		queryOptions({
			queryKey: collectorKeys.logSourceOptions(tenant),
			queryFn: ({ signal }) => fetchLogSourceOptions(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 5, 'm'),
			gcTime: time(10, 'm'),
		}),

	/**
	 * One diagnostic task — `GET /diagnostics/{task_id}`. Polls while the
	 * collector has not answered and stops at a final state.
	 *
	 * Never stale once final, so neither a remount nor a returning tab reads a
	 * finished task again. While waiting, the poll is what refreshes it.
	 */
	diagnostic: (tenant: string, taskId: string) =>
		queryOptions({
			queryKey: collectorKeys.diagnostic(tenant, taskId),
			queryFn: ({ signal }) => fetchDiagnostic(tenant, taskId, signal),
			staleTime: ({ state }) =>
				state.data && state.data.outcome !== 'waiting' ? Infinity : 0,
			gcTime: time(10, 'm'),
			refetchInterval: ({ state }) => diagnosticPollInterval(state.data),
			// The panel draws a failed read beside the button; throwing would take
			// the whole manage page down to its boundary over one panel.
			throwOnError: false,
		}),

	/**
	 * The tenant's trailing-24h volume, per collector. Its own query, never a
	 * field joined onto the list, so a failure here reaches only the volume
	 * tile and column. Polled on {@link VOLUME_POLL_INTERVAL}, not the status
	 * cadence — see there.
	 */
	fleetVolume: (tenant: string) =>
		queryOptions({
			queryKey: collectorKeys.fleetVolume(tenant),
			queryFn: ({ signal }) => fetchFleetVolume(tenant, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(10, 'm'),
			refetchInterval: VOLUME_POLL_INTERVAL,
		}),

	/** One collector's trailing-24h volume, per log source. As {@link collectorQueries.fleetVolume}. */
	collectorVolume: (tenant: string, collectorId: string) =>
		queryOptions({
			queryKey: collectorKeys.collectorVolume(tenant, collectorId),
			queryFn: ({ signal }) =>
				fetchCollectorVolume(tenant, collectorId, signal),
			staleTime: ({ state }) => successStaleTime(state, 1, 'm'),
			gcTime: time(10, 'm'),
			refetchInterval: VOLUME_POLL_INTERVAL,
		}),

	/**
	 * One bounded capture, read back until the collector answers —
	 * `GET /captures/{task_id}`.
	 *
	 * Polls while the task is waiting and stops at a final state. A final
	 * task never goes stale: the panel shows the one result it asked for, and
	 * a new capture is a new task with its own key.
	 */
	captureTask: (tenant: string, taskId: string) =>
		queryOptions({
			queryKey: collectorKeys.captureTask(tenant, taskId),
			queryFn: ({ signal }) => fetchCaptureTask(tenant, taskId, signal),
			staleTime: ({ state }) =>
				captureTaskPollInterval(state.data) === false ? Infinity : 0,
			gcTime: time(10, 'm'),
			refetchInterval: ({ state }) => captureTaskPollInterval(state.data),
		}),
}

/**
 * The Collectors **list page's** rows for the current tenant.
 * Suspends. See {@link collectorQueries.summaryList}.
 */
export function useCollectorSummaries() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.summaryList(tenant))
}

/**
 * One Collector for the current tenant. Suspends.
 *
 * `data` is `null` when the route answers 404 — the page's not-found state,
 * not an error (see `fetchCollectorDetail`).
 */
export function useCollector(collectorId: string) {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.detail(tenant, collectorId))
}

/**
 * One Log Source and the Collector hosting it, for the current tenant.
 * Suspends. See {@link collectorQueries.source} for the two not-found shapes.
 */
export function useLogSource(collectorId: string, sourceId: string) {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(
		collectorQueries.source(tenant, collectorId, sourceId),
	)
}

/**
 * The current tenant's Log Source device families. Suspends. An empty list
 * means the plan excludes syslog collection — see `../plan-upsell.ts`.
 */
export function useLogSourceOptions() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.logSourceOptions(tenant))
}

/**
 * One diagnostic task for the current tenant, or nothing while `taskId` is
 * `null`. Not suspending: the panel draws its own waiting state, and a run
 * the user has not asked for yet has no task to read.
 */
export function useDiagnostic(taskId: string | null) {
	const tenant = useCurrentTenant()
	return useQuery({
		...collectorQueries.diagnostic(tenant, taskId ?? ''),
		enabled: taskId !== null,
	})
}

/**
 * The current tenant's fleet volume. Suspends — the tile's read, behind its
 * own boundary.
 */
export function useFleetVolume() {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.fleetVolume(tenant))
}

/**
 * The same read, without suspending — for the per-collector figure on each
 * list row, which renders its own pending and error states in place rather
 * than hanging one boundary per row off a single shared request.
 */
export function useFleetVolumeSync() {
	const tenant = useCurrentTenant()
	// Never throw: the app defaults to throwing, which would carry a volume
	// failure past the row to the list's boundary and take the list with it.
	return useQuery({
		...collectorQueries.fleetVolume(tenant),
		throwOnError: false,
	})
}

/** One collector's volume. Suspends — the detail tile's read. */
export function useCollectorVolume(collectorId: string) {
	const tenant = useCurrentTenant()
	return useSuspenseQuery(collectorQueries.collectorVolume(tenant, collectorId))
}

/**
 * The same read, without suspending — for the log-source column. Never
 * throws, as {@link useFleetVolumeSync}.
 */
export function useCollectorVolumeSync(collectorId: string) {
	const tenant = useCurrentTenant()
	return useQuery({
		...collectorQueries.collectorVolume(tenant, collectorId),
		throwOnError: false,
	})
}

/**
 * One capture task for the current tenant, polled while it waits. Does not
 * suspend: the panel's own waiting state is the loading state, and a capture
 * that has not been asked for yet (`taskId` undefined) reads nothing.
 */
export function useCaptureTask(taskId: string | undefined) {
	const tenant = useCurrentTenant()
	return useQuery({
		...collectorQueries.captureTask(tenant, taskId ?? ''),
		enabled: taskId !== undefined,
	})
}
